// ============================================================
//  DASHBOARD LAYOUT — ADMIN DASHBOARD
//  Path: src/layouts/DashboardLayout.tsx
//
//  FIX (review pass):
//  - Added "Pending Certification" (/pending-certification) — the page
//    existed but had no route or menu entry, so its queue was invisible.
//  - With 24 entries the menu is taller than most screens, and the whole
//    page scrolled with it — Log Out ended up far below the fold. The
//    sidebar now stays put at screen height and the menu scrolls inside.
//  - Collapsed mode showed icons only, with no names, and two of them
//    were the same 🛡️ (Background Checks and Admins). Each item now has
//    its name as a tooltip / screen-reader label, and Admins uses 🔑.
//  - Log Out did nothing if signing out failed (e.g. offline); it now
//    always leaves for the login page.
//
//  CHANGE: added "Tax Report" (/tax-report) and "Tax Rates" (/tax-rates) — both
//  pages existed with routes but no menu entry.
//
//  CHANGE: added "Disputes" (/tasks?status=disputed) — tasks waiting for
//  an admin decision (completion disputes and no-show reports sent to
//  review). It opens the Tasks page already filtered. Both items point at
//  /tasks, so the highlight is decided by the URL's status filter: only
//  "Disputes" lights up on the disputed view, only "Tasks" otherwise.
//
//  CHANGE (2026-10-10): the Support item shows a red number — help requests
//  from taskers and customers that nobody has answered yet (status "open"),
//  so staff see new messages without opening Support. Checked when the
//  dashboard opens, every minute, and when the browser tab comes back into
//  view; SupportPage also announces the number after each change
//  (support.api.ts SUPPORT_OPEN_COUNT_EVENT) so it updates at once.
// ============================================================

import React, { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getSupportOpenSummaryApi, SUPPORT_OPEN_COUNT_EVENT } from '../api/support.api'

const SUPPORT_BADGE_REFRESH_MS = 60_000
const SUPPORT_ROLES = ['super_admin', 'admin', 'customer_service']

const navItems = [
  { path: '/',                  label: 'Dashboard',          icon: '📊', roles: ['super_admin', 'admin'] },
  { path: '/payments',          label: 'Payments',           icon: '💰', roles: ['super_admin', 'admin'] },
  { path: '/transactions',      label: 'Transaction History', icon: '🧾', roles: ['super_admin', 'admin'] },
  { path: '/reports',           label: 'Reports',            icon: '📈', roles: ['super_admin', 'admin'] },
  // GST/HST/QST audit report (per-component ledger, CSV) and the read-only province rate table.
  { path: '/tax-report',        label: 'Tax Report',         icon: '🧮', roles: ['super_admin', 'admin'] },
  // Per hired task: who covers the worker's insurance, who was hired, how the money is split.
  { path: '/task-coverage',     label: 'Task Coverage',      icon: '🧾', roles: ['super_admin', 'admin'] },
  { path: '/tax-rates',         label: 'Tax Rates',          icon: '🏛️', roles: ['super_admin', 'admin'] },
  { path: '/tasks',             label: 'Tasks',              icon: '📋', roles: ['super_admin', 'admin', 'customer_service'] },
  // Same page, filtered. customer_service can see disputes (read-only —
  // only super_admin/admin can resolve, enforced by the backend too).
  { path: '/tasks?status=disputed', label: 'Disputes',       icon: '⚖️', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/background-checks', label: 'Background Checks', icon: '🛡️', roles: ['super_admin', 'admin', 'customer_service'] },
  // Certify is super_admin/admin only; customer_service sees the queue
  // in the page's View Only mode (same roles as App.tsx's route).
  { path: '/pending-certification', label: 'Pending Certification', icon: '🏅', roles: ['super_admin', 'admin', 'customer_service'] },
  // Uploaded insurance documents + whether each one removes the tasker's insurance fee.
  // Separate from approving the person.
  { path: '/insurance', label: 'Insurance', icon: '📑', roles: ['super_admin', 'admin', 'customer_service'] },
  // Per-job policies bought from the connected insurer, and any that failed.
  { path: '/insurer', label: 'Insurer policies', icon: '🛡️', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/vehicle-verifications', label: 'Vehicle Verifications', icon: '🚗', roles: ['super_admin', 'admin', 'customer_service'] },
  // Same roles as the backend route (super_admin, admin only) — not
  // customer_service, since re-queuing a stuck scan is operational,
  // not a view.
  { path: '/scan-failed-uploads', label: 'Scan-Failed Uploads', icon: '🔁', roles: ['super_admin', 'admin'] },
  { path: '/past-due-subscriptions', label: 'Past-Due Subscriptions', icon: '⚠️', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/categories',   label: 'Categories',         icon: '🗂️', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/communication', label: 'Communication',      icon: '💬', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/tasker-breakdown', label: 'Tasker Coverage', icon: '🗺️', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/geography',        label: 'Geography',        icon: '🌍', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/advertisements',   label: 'Advertisements',   icon: '📢', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/spotlights',       label: 'Spotlights',       icon: '⭐', roles: ['super_admin', 'admin'] },
  { path: '/users',             label: 'Users',              icon: '👥', roles: ['super_admin', 'admin'] },
  { path: '/customers',         label: 'Customers',          icon: '🛒', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/experts',           label: 'Experts',            icon: '🔧', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/companies',         label: 'Companies',          icon: '🏢', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/worker-directory',  label: 'Worker Directory',   icon: '🧰', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/admins',            label: 'Admins',             icon: '🔑', roles: ['super_admin'] },
  { path: '/version-control',   label: 'Version Control',    icon: '🔢', roles: ['super_admin', 'admin'] },
  // Help requests from the apps, and one-way messages to users.
  { path: '/support',          label: 'Support',            icon: '🛟', roles: ['super_admin', 'admin', 'customer_service'] },
  { path: '/settings',          label: 'Settings',           icon: '⚙️', roles: ['super_admin', 'admin', 'customer_service'] },
]

const DashboardLayout: React.FC = () => {
  const { user, logout }          = useAuth()
  const navigate                  = useNavigate()
  const location                  = useLocation()
  const onDisputesView = location.pathname === '/tasks' && new URLSearchParams(location.search).get('status') === 'disputed'
  // NavLink compares the path only, so "Tasks" and "Disputes" would both
  // light up on /tasks — the status filter decides which one is active.
  const isItemActive = (itemPath: string, navLinkActive: boolean) => {
    if (itemPath === '/tasks?status=disputed') return onDisputesView
    if (itemPath === '/tasks') return navLinkActive && !onDisputesView
    return navLinkActive
  }
  const [collapsed, setCollapsed] = useState(false)

  // Red number on the Support item (see header).
  const [supportOpen, setSupportOpen] = useState(0)
  const canSeeSupport = (user?.roles || []).some((r: string) => SUPPORT_ROLES.includes(r))
  useEffect(() => {
    if (!canSeeSupport) return
    let alive = true
    const refresh = () => {
      getSupportOpenSummaryApi()
        .then((res) => { if (alive) setSupportOpen(Number(res.data?.data?.openCount) || 0) })
        .catch(() => { /* keep the last number; the next check retries */ })
    }
    refresh()
    const timer = setInterval(refresh, SUPPORT_BADGE_REFRESH_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
    const onAnnounce = (e: Event) => {
      const n = Number((e as CustomEvent).detail?.count)
      if (Number.isFinite(n)) setSupportOpen(n)
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener(SUPPORT_OPEN_COUNT_EVENT, onAnnounce)
    return () => {
      alive = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener(SUPPORT_OPEN_COUNT_EVENT, onAnnounce)
    }
  }, [canSeeSupport])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (err) {
      console.error('Sign-out failed:', err)
    } finally {
      navigate('/login', { replace: true })
    }
  }

  const userRoles = user?.roles || []
  const filteredNav = navItems.filter(item => item.roles.some(r => userRoles.includes(r)))

  const getAdminRole = () => {
    if (userRoles.includes('super_admin')) return 'super admin'
    if (userRoles.includes('admin')) return 'admin'
    if (userRoles.includes('customer_service')) return 'customer service'
    return ''
  }

  return (
    <div className="min-h-screen bg-surface-primary flex">
      <aside className={`${collapsed ? 'w-20' : 'w-64'} sticky top-0 h-screen flex-shrink-0 bg-surface-secondary border-r border-border-light flex flex-col transition-all duration-200`}>
        <div className="h-16 flex-shrink-0 flex items-center px-5 border-b border-border-light">
          {!collapsed && <span className="text-xl font-bold text-white">🛡️ Admin</span>}
          {collapsed && <span className="text-xl mx-auto">🛡️</span>}
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto py-4 px-3">
          {filteredNav.map((item) => (
            <NavLink key={item.path} to={item.path} end={item.path === '/'}
              title={collapsed ? item.label : undefined}
              aria-label={item.path === '/support' && supportOpen > 0 ? `${item.label}, ${supportOpen} not answered` : item.label}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-xl mb-1 transition-colors ${
                  isItemActive(item.path, isActive) ? 'bg-brand-600/20 text-brand-400' : 'text-gray-400 hover:bg-surface-tertiary hover:text-white'
                }`
              }>
              <span className="relative text-lg" aria-hidden="true">
                {item.icon}
                {/* Collapsed menu: a small number on the icon instead. */}
                {collapsed && item.path === '/support' && supportOpen > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-[18px] text-center">
                    {supportOpen > 99 ? '99+' : supportOpen}
                  </span>
                )}
              </span>
              {!collapsed && <span className="font-medium">{item.label}</span>}
              {!collapsed && item.path === '/support' && supportOpen > 0 && (
                <span
                  className="ml-auto min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold leading-[22px] text-center"
                  title={`${supportOpen} help request${supportOpen === 1 ? '' : 's'} not answered yet`}
                >
                  {supportOpen > 99 ? '99+' : supportOpen}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <button onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand menu' : undefined}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          className="flex-shrink-0 px-3 py-3 mx-3 mb-2 text-gray-500 hover:text-white hover:bg-surface-tertiary rounded-xl transition-colors text-sm">
          {collapsed ? '→' : '← Collapse'}
        </button>

        <div className="flex-shrink-0 border-t border-border-light p-4">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-sm font-medium text-white truncate">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              <p className="text-xs text-brand-400 mt-1">{getAdminRole()}</p>
            </div>
          )}
          <button onClick={handleLogout}
            title={collapsed ? 'Log Out' : undefined}
            aria-label="Log Out"
            className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
            {collapsed ? '🚪' : '🚪 Log Out'}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="p-8"><Outlet /></div>
      </main>
    </div>
  )
}

export default DashboardLayout